// Sala de espera: lugares em volta da mesa, convite pelo WhatsApp e o botão de começar do dono.
// Também serve para quem chega com a partida andando e para o fim da partida: com vaga aberta, quem assiste toca
// Sentar na vaga e entra na próxima mão.

import { SeloNoTopo } from '../comum/SeloGratis.tsx';
import { meuCodigoDeIndicacao } from '../comum/limite.ts';
import { ligaNoAr } from '../comum/liga.ts';
import { Fragment, useState } from 'react';
import { team } from '@truco6/regras';
import type { Seat } from '@truco6/regras';
import type { LugarPublico, OrigemDaVaga, SalaPublica } from '@truco6/mesa/protocolo';
import { conta, type Sessao } from '../comum/conta.ts';
import { medir } from '../comum/medicao.ts';
import { ir, linkDeConviteDaSala } from '../comum/rotas.ts';
import { Rosto } from '../comum/Rosto.tsx';
import { BotaoAmizade, IconeAmigos } from '../comum/BotaoAmizade.tsx';
import { FolhaChamarAmigos } from './Amigos.tsx';
import { agoraNoServidor, useAgora, useMesa } from '../comum/useMesa.ts';
import type { ConexaoMesa } from '../mesa/conexao.ts';
import { apelidoDe, ehBot, ehVaga, esperaAMao, regrasDaCasa, rostoDoLugar, vagasAbertas } from '../mesa/textos.ts';
import { FaixaLiga, NOTA_SEM_RANKING } from '../liga/comum.tsx';
import { FaixaCampeonato, vencedorDoConfronto } from '../liga/campeonatos.tsx';
import { Tento } from './tentos-comum.tsx';
import { Tela } from './comum.tsx';

// Lugar relativo: 0 embaixo e, no sentido da mesa, esquerda, topo e direita.
const POS: [number, number][] = [[160, 268], [42, 204], [42, 92], [160, 0], [278, 92], [278, 204]];

function nomeDoDono(sala: SalaPublica): string {
  const l = sala.lugares.find((o) => o?.tipo === 'pessoa' && o.id === sala.dono);
  if (l) return l.perfil.apelido;
  return sala.esperando.find((x) => x.id === sala.dono)?.perfil.apelido ?? 'o dono da sala';
}

// origem: por onde a pessoa chegou, para o evento da vaga ocupada. recado: a vaga que ela veio buscar já tinha dono.
export function Espera({ c, sessao, origem = 'link', recado = null }: { c: ConexaoMesa; sessao: Sessao; origem?: OrigemDaVaga; recado?: string | null }) {
  const e = useMesa(c);
  useAgora();
  const agora = agoraNoServidor(e);
  const [copiado, setCopiado] = useState<string | null>(null);
  const [chamando, setChamando] = useState(false);
  const sala = e.sala!, eu = e.eu, link = linkDeConviteDaSala(sala.codigo, meuCodigoDeIndicacao());
  const base = eu ?? 0, meuTrio = team(base as Seat);
  const souDono = e.usuario === sala.dono, fase = sala.fase;
  const pessoas = sala.lugares.filter((l) => l?.tipo === 'pessoa').length;
  const inicio = e.prazos.find((p) => p.tipo === 'inicio');
  const t = sala.tentos, reservei = !!t && !!e.usuario && t.reservados.includes(e.usuario);
  const podeComecar = (fase === 'espera' || fase === 'fim') && pessoas > 0 && (sala.regras.vagasComBot || pessoas === 6);
  // Valendo Tentos e campeonato nunca têm bot na vaga: isso fica na nota, não nas regras da casa.
  const daCasa = regrasDaCasa(t || sala.campeonato ? { ...sala.regras, vagasComBot: true } : sala.regras);
  // Mesa da liga: cada um tem o lugar da busca e a partida começa sozinha. Sala de campeonato: os 6 lugares são dos
  // dois trios do confronto, sem vaga, e a partida começa sozinha com os 6 na mesa.
  const liga = sala.liga ?? null, camp = sala.campeonato ?? null, fixo = !!liga || !!camp;
  const chegaram = sala.lugares.filter((l) => l?.tipo === 'pessoa' && l.conectado).length;
  // Com a partida andando, só a vaga aberta aceita quem assiste (valendo Tentos, na liga e no campeonato, nunca por aqui).
  const assumivel = (l: LugarPublico | null) => !t && !fixo && eu === null && ehVaga(l);
  const vagas = fase === 'jogo' && !t && !fixo ? vagasAbertas(sala.lugares) : [];

  function sentar(s: Seat, l: LugarPublico | null) {
    if (fixo) return;
    if (eu === s) { if (fase !== 'jogo') c.enviar({ t: 'levantar' }); return; }
    if (fase === 'jogo' ? assumivel(l) : !l || (l.tipo === 'bot' && fase === 'fim')) c.enviar({ t: 'sentar', lugar: s, ...(fase === 'jogo' ? { origem } : {}) });
  }
  async function copiar() {
    try { await navigator.clipboard.writeText(link); setCopiado('Link copiado!'); }
    catch { setCopiado('Copie o link: ' + link); }
    medir('share', { method: 'link', content_type: 'sala' });
    void conta.registrarConvite(sessao, { canal: 'link', tipo: 'sala', mesa: sala.codigo }).catch(() => {});
    setTimeout(() => setCopiado(null), 2500);
  }

  let status: string;
  if (camp) {
    const vencedor = vencedorDoConfronto(camp);
    status = vencedor ? `Jogo decidido: vitória de ${vencedor}.`
      : fase === 'encerrada' ? 'Este jogo do campeonato acabou.'
        : inicio ? `${fase === 'fim' ? 'A próxima partida' : 'A partida'} começa em ${Math.max(0, Math.ceil((inicio.ate - agora) / 1000))}s.`
          : fase === 'jogo' ? 'A partida já começou.' : `Esperando os dois trios: ${chegaram} de 6 na mesa. A partida começa sozinha com os 6.`;
  } else if (liga) {
    status = fase === 'encerrada' ? 'Esta mesa da liga fechou. Jogue de novo na liga.'
      : inicio ? `Esperando a mesa chegar. A partida começa em ${Math.max(0, Math.ceil((inicio.ate - agora) / 1000))}s.`
        : fase === 'jogo' ? 'A partida já começou.' : 'A partida começa assim que todos chegarem.';
  } else if (fase === 'jogo') {
    const placar = sala.placar ? `Partida em andamento: ${String(sala.placar[0]).padStart(2, '0')} x ${String(sala.placar[1]).padStart(2, '0')}.` : 'A partida já começou.';
    status = vagas.length && eu === null ? `${placar} Tem vaga aberta: quem sentar entra na próxima mão.` : `${placar} Espere a próxima.`;
  }
  else if (e.prazos.some((p) => p.tipo === 'contagem')) status = 'A revanche vai começar.';
  else if (sala.confirmarBots) status = souDono ? 'Deseja iniciar com bots?' : `${nomeDoDono(sala)} está decidindo se começa com bots.`;
  else if (inicio) status = `Mesa completa! A partida começa em ${Math.max(0, Math.ceil((inicio.ate - agora) / 1000))}s.`;
  else if (fase === 'fim' && sala.revanche) {
    const s = Math.max(0, Math.ceil((sala.revanche.ate - agora) / 1000));
    status = `Revanche: ${sala.revanche.sim.length} de ${pessoas} ${sala.revanche.sim.length === 1 ? 'confirmou' : 'confirmaram'}. Fecha em ${s}s.`;
  } else if (fase === 'fim') status = 'A partida acabou. Sente-se para a próxima.';
  else if (fase === 'encerrada') status = sala.removida ? 'A sala foi encerrada na revisão do nome.' : 'O dono saiu e a sala foi encerrada.';
  else status = eu === null ? 'Escolha um lugar na mesa.' : souDono ? 'Quando quiser, comece a partida.' : `Esperando ${nomeDoDono(sala)} começar a partida.`;

  return (
    <Tela larga>
      {liga ? <FaixaLiga liga={liga} /> : camp ? <FaixaCampeonato c={camp} /> : (
        <header className="topo-sala">
          <div>
            <span>{sala.nome}</span>
            <b aria-label={'Código da sala ' + sala.codigo.split('').join(' ')}>{sala.codigo}</b>
          </div>
          <button className="chipbtn" onClick={copiar}>Copiar</button>
        </header>
      )}
      {!liga && !camp && <SeloNoTopo indique={false} />}
      <div className="espera">
        <section className="espera-mesa" aria-label="Lugares da mesa">
          {vagas.length > 0
            ? <p className="contagem"><b className="dourado">{vagas.length} {vagas.length === 1 ? 'vaga' : 'vagas'}</b> na partida em andamento</p>
            : <p className="contagem"><b>{camp ? chegaram : pessoas} de 6</b> na mesa</p>}
          <div className="mini-mesa">
            <div className="hex mini-madeira" aria-hidden="true" />
            <div className="hex felt mini-feltro" aria-hidden="true" />
            {sala.lugares.map((l, i) => {
              const s = i as Seat, pos = POS[(s - base + 6) % 6]!, nos = team(s) === meuTrio, euAqui = eu === s;
              const cor = nos ? 'var(--nos-400)' : 'var(--eles-400)';
              const livre = !fixo && (fase === 'jogo' ? assumivel(l) : !l || (l.tipo === 'bot' && fase === 'fim'));
              const nome = euAqui ? 'Você' : esperaAMao(l) ? l!.perfil.apelido : l ? apelidoDe(l) : 'Vaga';
              const acao = fixo ? '' : euAqui ? (fase !== 'jogo' ? 'Levantar' : '') : livre ? (fase === 'jogo' ? 'Sentar na vaga' : 'Sentar') : '';
              const r0 = rostoDoLugar(l), vaga = fase === 'jogo' && ehVaga(l);
              // O botão de amizade fica ao lado do rosto, do lado de dentro da mesa, sem cobrir o lugar (o lugar já é o botão de sentar).
              const amigo = l?.tipo === 'pessoa' && l.id !== e.usuario && !euAqui ? l : null;
              return (
                <Fragment key={i}>
                {amigo && (
                  <div className="lugar-amizade" style={{ left: pos[0] + (pos[0] > 200 ? -70 : 70), top: pos[1] }}>
                    <BotaoAmizade usuario={amigo.id} apelido={amigo.perfil.apelido} compacto />
                  </div>
                )}
                <button className={'lugar' + (euAqui ? ' meu' : '') + (l ? '' : ' vazio') + (vaga ? ' vaga' : '')} style={{ left: pos[0], top: pos[1] }}
                  disabled={!acao} aria-label={`${nos ? 'NÓS' : 'ELES'}, lugar ${i + 1}: ${nome}${acao ? '. ' + acao : ''}`} onClick={() => sentar(s, l)}>
                  {l ? (
                    <span style={l.tipo === 'pessoa' && !l.conectado && !l.bot ? { opacity: 0.5 } : undefined}>
                      <Rosto {...r0} cor={cor} tamanho={50} />
                    </span>
                  ) : <span className="mais" style={{ borderColor: cor, color: cor }}>+</span>}
                  <span className="nm">{nome}</span>
                  <span className="trio-tag" style={{ color: cor }}>
                    {nos ? 'NÓS' : 'ELES'}
                    {vaga ? ' · VAGA ABERTA' : esperaAMao(l) ? ' · PRÓXIMA MÃO' : ehBot(l) ? ' · BOT' : ''}
                    {acao && acao !== 'Levantar' && !vaga ? ' · ' + acao.toUpperCase() : ''}
                  </span>
                </button>
                </Fragment>
              );
            })}
          </div>
          {sala.esperando.length > 0 && (
            <p className="nota">Na sala, sem lugar: {sala.esperando.map((x) => (x.id === e.usuario ? 'você' : x.perfil.apelido)).join(', ')}</p>
          )}
        </section>
        <section className="espera-acoes">
          {t && (
            <div className="faixa-tentos" aria-label={`Valendo Tentos: ${t.ingresso} por jogador, pote ${t.pote}, casa ${t.casa}, cada vencedor leva ${t.premio}`}>
              <Tento tamanho={28} brilho />
              <p><b>Valendo Tentos · {t.ingresso} por jogador</b><span>Pote {t.pote} · Casa {t.casa} · Cada vencedor leva {t.premio}</span></p>
            </div>
          )}
          {t && reservei && fase !== 'jogo' && <p className="nota">Seu ingresso de {t.ingresso} Tentos está reservado. Se sair da sala, ele volta para o saldo.</p>}
          {t && !reservei && eu === null && fase !== 'jogo' && fase !== 'encerrada' && (
            <button className="btn1 largo" onClick={() => c.enviar({ t: 'reservar' })}>RESERVAR INGRESSO · {t.ingresso}</button>
          )}
          <p className="status" role="status">{status}</p>
          {vagas.length > 0 && eu === null && (
            <button className="btn1 largo" onClick={() => c.enviar({ t: 'sentar', lugar: vagas[0]!, origem })}>SENTAR NA VAGA</button>
          )}
          {recado && !e.aviso && eu === null && <p className="erro" role="alert">{recado}</p>}
          {fase === 'jogo' && eu === null && !vagas.length && !fixo && (
            <div className="cheia-assistir">
              <p><b>A mesa está cheia. Quer assistir?</b></p>
              <button className="btn1 largo" onClick={() => { c.enviar({ t: 'sair' }); c.encerrar(); ir({ tela: 'assistir', codigo: sala.codigo, amigo: null }); }}>ASSISTIR</button>
            </div>
          )}
          {!liga && daCasa.length > 0 && <p className="regras-sala"><b>Regras da casa:</b> {daCasa.join(', ')}.</p>}
          {e.aviso && <p className="erro" role="alert">{e.aviso.mensagem}</p>}
          {liga && fase === 'encerrada' && (ligaNoAr()
            ? <button className="btn1 largo" onClick={() => { c.encerrar(); ir({ tela: 'liga', trio: null }); }}>JOGAR NA LIGA</button>
            : <button className="btn1 largo" onClick={() => { c.encerrar(); ir({ tela: 'inicio' }); }}>VER SALAS ABERTAS</button>)}
          {camp && (camp.decidido || fase === 'encerrada') && (
            <button className="btn1 largo" onClick={() => { c.enviar({ t: 'sair' }); c.encerrar(); ir({ tela: 'campeonato', slug: camp.slug }); }}>VER O CAMPEONATO</button>
          )}
          {!fixo && <div className="dupla">
            <a className="btn2 whats" href={'https://wa.me/?text=' + encodeURIComponent('Bora jogar truco de 6? Entra na mesa: ' + link)}
              target="_blank" rel="noreferrer" onClick={() => { medir('share', { method: 'whatsapp', content_type: 'sala' }); void conta.registrarConvite(sessao, { canal: 'whatsapp', tipo: 'sala', mesa: sala.codigo }).catch(() => {}); }}>
              Convidar pelo WhatsApp
            </a>
            <button className="btn2" onClick={copiar}>Copiar link</button>
          </div>}
          {copiado && <p className="ok" role="status">{copiado}</p>}
          {!fixo && !sessao.anonimo && fase === 'espera' && pessoas < 6 && (
            <button className="btn2 largo chamar-amigos" onClick={() => setChamando(true)}><IconeAmigos tamanho={20} />Chamar amigos</button>
          )}
          {chamando && <FolhaChamarAmigos sessao={sessao} aoFechar={() => setChamando(false)} />}
          {souDono && !liga && fase !== 'jogo' && (!t || reservei) && (
            <button className="btn1 largo" disabled={!podeComecar} onClick={() => c.enviar({ t: 'iniciar' })}>
              {fase === 'fim' ? 'COMEÇAR AGORA' : 'COMEÇAR PARTIDA'}
              {podeComecar && pessoas < 6 && <small> com {6 - pessoas} {6 - pessoas === 1 ? 'bot' : 'bots'}</small>}
            </button>
          )}
          {!liga && !t && !camp && fase !== 'jogo' && fase !== 'encerrada' && sala.regras.vagasComBot && 6 - pessoas >= 3 && (
            <p className="nota">{NOTA_SEM_RANKING}</p>
          )}
          <p className="nota">
            {liga ? 'Mesa da liga: até 12 pontos, 20 s por jogada e sinais em Só o aviso. Vaga de quem não chegar fica com um bot.'
              : camp ? 'Jogo de campeonato, sem vaga com bot. Quem cair tem 45 s para voltar; depois, o bot joga e o jogo vale. O trio que não completar a mesa a tempo perde por W.O.'
              : t ? 'Valendo Tentos começa com 6 pessoas, sem bot.' : sala.regras.vagasComBot
              ? 'Vaga vazia vira bot quando a partida começa. Quem chegar depois senta no lugar do bot e entra na próxima mão.'
              : 'Esta sala só começa com 6 pessoas.'}
            {!fixo && ' Com 6 pessoas sentadas, a partida começa sozinha em 5 s.'}
          </p>
          <button className="link" onClick={() => { c.enviar({ t: 'sair' }); c.encerrar(); ir({ tela: 'inicio' }); }}>Sair da sala</button>
        </section>
        {souDono && sala.confirmarBots && fase === 'espera' && <PerguntaDosBots c={c} vazios={6 - pessoas} />}
      </div>
    </Tela>
  );
}

// Revanche incompleta: quem estava na mesa não confirmou e o lugar dele ficaria com um bot. Só o dono decide. Sim, a
// contagem começa e os bots entram; Não, a mesa espera na sala, com o convite pelo WhatsApp e o Chamar amigos.
function PerguntaDosBots({ c, vazios }: { c: ConexaoMesa; vazios: number }) {
  const [enviado, setEnviado] = useState(false);
  const responder = (sim: boolean) => { setEnviado(true); c.enviar({ t: 'bots', sim }); };
  return (
    <div className="pergunta-fundo">
      <div className="revanche pergunta-bots" role="alertdialog" aria-modal="true" aria-labelledby="titulo-bots" aria-describedby="texto-bots">
        <h2 id="titulo-bots">Deseja iniciar com bots?</h2>
        <p id="texto-bots">
          {vazios === 1 ? '1 lugar ficou vazio' : `${vazios} lugares ficaram vazios`} na revanche. Sim: a contagem começa e os bots
          entram. Não: a mesa espera, e você chama os amigos.
        </p>
        <div className="botoes-revanche">
          <button className="sim" disabled={enviado} autoFocus onClick={() => responder(true)}>SIM</button>
          <button className="nao" disabled={enviado} onClick={() => responder(false)}>NÃO</button>
        </div>
      </div>
    </div>
  );
}
